import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
    FaFileInvoiceDollar, FaEdit, FaFilePdf, FaHistory, FaTrash, FaPlusCircle, FaSearch, FaExclamationCircle, FaCheckCircle, FaEye, FaBan
} from 'react-icons/fa';
import { 
    fetchFactures, createFacture, updateFacture, deleteFacture, fetchClients, fetchPrestations, exportFacturePDF 
} from '../api'; 

const FacturePage = ({ user }) => {
    const location = useLocation();
    
    const [factures, setFactures] = useState([]);
    const [clients, setClients] = useState([]);
    const [prestations, setPrestations] = useState([]);
    
    const [clientSearch, setClientSearch] = useState('');
    const [currentLines, setCurrentLines] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState(''); 
    const [prestationSearchTerm, setPrestationSearchTerm] = useState(''); 
    const [showSuggestions, setShowSuggestions] = useState(false);        
    const [selectedPrestation, setSelectedPrestation] = useState(null);   
    const [remise, setRemise] = useState(0); 

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); 
    
    const [downloadingPdfId, setDownloadingPdfId] = useState(null);
    const [viewInvoice, setViewInvoice] = useState(null);

    // 🔒 GESTION DES RÔLES & PERMISSIONS
    const userEmail = user?.email?.toLowerCase() || user?.username?.toLowerCase() || '';
    const isKhKoffi = userEmail === 'kh.koffi@arsn.ci' || userEmail === 'kh.koffi@gmail.com';
    const isSuperAdmin = user?.role === 'superadmin';
    const isAdmin = user?.role === 'admin' || isSuperAdmin;
    
    const isSpecialUser = userEmail === 'rd.ble@arsn.ci' || userEmail === 'kb.ouattara@arsn.ci';

    // 1. Droit d'annulation : Réservé aux SuperAdmins, Admins et KhKoffi
    const canCancel = isKhKoffi || isSuperAdmin || (user?.role === 'admin' && !isSpecialUser);

    // 2. Droit de suppression
    const canDeleteInvoice = (f) => {
        if (f.status === 'payée') return false; 
        if (f.status === 'annulée') return isSuperAdmin; 
        return isAdmin; 
    };

    // 3. Règle de modification : Ouverte à TOUT le monde (tant que la facture n'est ni payée ni annulée)
    const canEditInvoice = (f) => {
        if (f.status === 'payée' || f.status === 'annulée') return false;
        return true;
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [resFac, resCli, resPrest] = await Promise.all([
                fetchFactures(), fetchClients(), fetchPrestations()
            ]);
            const facturesDefinitives = (resFac.data || []).filter(f => f.type !== 'avoir' && f.type !== 'proforma');
            setFactures(facturesDefinitives);
            setClients(resCli.data);
            setPrestations(resPrest.data);
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur de connexion au serveur.' });
        } finally { setLoading(false); }
    };

    useEffect(() => { 
        loadData(); 
        if (location.state && location.state.incomingPrestations) {
            const lines = location.state.incomingPrestations.map(p => ({
                prestationId: p._id, code: p.code, name: p.name, details: p.details || '',
                price: p.price, quantity: 1, total: p.price
            }));
            setCurrentLines(lines);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const filteredPrestations = prestationSearchTerm.length > 0
        ? prestations.filter(p => p.name.toLowerCase().includes(prestationSearchTerm.toLowerCase()) || p.code.toLowerCase().includes(prestationSearchTerm.toLowerCase())).slice(0, 50) 
        : [];

    const filteredFactures = factures.filter(f => 
        (f.number.toLowerCase().includes(search.toLowerCase()) || f.clientName.toLowerCase().includes(search.toLowerCase())) && 
        (statusFilter === 'all' || f.status === statusFilter)
    );

    const handleSelectSuggestion = (p) => {
        setSelectedPrestation(p);          
        setPrestationSearchTerm(p.name);    
        setShowSuggestions(false);          
    };

    const handleAddPrestation = () => {
        if (!selectedPrestation) return;
        const newLine = { 
            prestationId: selectedPrestation._id, code: selectedPrestation.code, name: selectedPrestation.name, 
            details: selectedPrestation.details || '', price: Number(selectedPrestation.price), 
            quantity: 1, total: Number(selectedPrestation.price) 
        };
        setCurrentLines([...currentLines, newLine]);
        setSelectedPrestation(null); setPrestationSearchTerm(''); setMessage(null); 
    };

    const updateLine = (idx, field, val) => {
        const newLines = [...currentLines];
        newLines[idx][field] = val;
        if (field === 'price' || field === 'quantity') {
            newLines[idx].total = Number(newLines[idx].price) * Number(newLines[idx].quantity);
        }
        setCurrentLines(newLines);
    };

    const removeLine = (idx) => setCurrentLines(currentLines.filter((_, i) => i !== idx));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientSearch || currentLines.length === 0) return;

        const payload = { 
            clientName: clientSearch, lines: currentLines, 
            date: invoiceDate, dueDate: dueDate, type: 'final',
            remise: Number(remise) || 0 
        };

        try {
            if (editingId) { 
                await updateFacture(editingId, payload); 
                setMessage({ type: 'success', text: '✅ Mise à jour réussie' }); 
            } else { 
                await createFacture(payload); 
                setMessage({ type: 'success', text: '✅ Facture émise avec succès !' }); 
            }
            
            await loadData(); 
            
            setTimeout(() => {
                setEditingId(null); 
                setClientSearch(''); 
                setCurrentLines([]); 
                setDueDate(''); 
                setRemise(0); 
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 50);

        } catch (err) { 
            const errorText = err.response?.data?.message || 'Erreur lors de l\'enregistrement.';
            setMessage({ type: 'error', text: errorText }); 
        }
    };

    const handleDownloadPDF = async (id, number) => {
        setDownloadingPdfId(id);
        try {
            const res = await exportFacturePDF(id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a'); 
            link.href = url;
            link.setAttribute('download', `Facture_${number.replace(/\//g, '-')}.pdf`);
            document.body.appendChild(link); 
            link.click();
            link.remove(); 
            window.URL.revokeObjectURL(url); 
        } catch (err) { 
            setMessage({ type: 'error', text: "Erreur lors du téléchargement du PDF." }); 
        } finally {
            setDownloadingPdfId(null);
        }
    };

    const handleCancel = async (f) => {
        if (!canCancel) {
            setMessage({ type: 'error', text: '⛔ Action refusée : Vous n\'avez pas les droits pour annuler une facture.' });
            return;
        }
        if (f.status === 'payée') {
            setMessage({ type: 'error', text: '⛔ Impossible d\'annuler une facture déjà payée.' });
            return;
        }
        if (!window.confirm(`⚠️ Voulez-vous vraiment ANNULER la facture ${f.number} ?`)) return;
        try { 
            await updateFacture(f._id, { status: 'annulée' }); 
            loadData(); 
            setMessage({ type: 'success', text: '✅ Facture annulée avec succès.' }); 
        } catch (e) { 
            setMessage({ type: 'error', text: e.response?.data?.message || 'Erreur lors de l\'annulation.' }); 
        }
    };

    const handleDelete = async (f) => {
        if (!canDeleteInvoice(f)) {
            setMessage({ type: 'error', text: '⛔ Action refusée.' });
            return;
        }

        if (!window.confirm("⚠️ ATTENTION : La suppression d'une facture détruit l'enregistrement. Confirmer ?")) return;
        try { 
            await deleteFacture(f._id); 
            loadData(); 
            setMessage({ type: 'success', text: '✅ Facture supprimée avec succès.' }); 
        } catch (e) { 
            setMessage({ type: 'error', text: e.response?.data?.message || 'Erreur lors de la suppression.' }); 
        }
    };

    const startEdit = (f) => {
        if (!canEditInvoice(f)) {
            setMessage({ type: 'error', text: `⛔ Action refusée : Impossible de modifier cette facture (${f.status}).` });
            return;
        }
        setEditingId(f._id);
        setClientSearch(f.clientName);
        setCurrentLines(f.lines);
        setRemise(f.remise || 0); 
        if(f.date) setInvoiceDate(new Date(f.date).toISOString().split('T')[0]);
        if(f.dueDate) setDueDate(new Date(f.dueDate).toISOString().split('T')[0]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setMessage({ type: 'warning', text: "✏️ MODE ÉDITION" });
    };

    const totalHTBrut = currentLines.reduce((acc, l) => acc + (l.total || 0), 0);
    const numRemise = Number(remise) || 0;
    const totalTTC = Math.max(0, totalHTBrut - numRemise); 

    return (
        <div className="container-fluid py-4 px-3 px-md-4" translate="no">
            
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaFileInvoiceDollar className="me-2 text-primary"/> FACTURES DÉFINITIVES
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Gestion et émission des documents officiels</p>
                </div>
            </div>

            <div style={{ minHeight: '50px', marginBottom: '1rem' }}>
                <div 
                    className={`alert py-2 mb-0 shadow-sm border-0 ${message?.type === 'error' ? 'alert-danger' : (message?.type === 'warning' ? 'alert-warning' : 'alert-success')}`}
                    style={{ display: message ? 'block' : 'none' }}
                >
                    {message?.text}
                </div>
            </div>

            {/* FORMULAIRE D'ÉMISSION / EDITION */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-primary" style={{ letterSpacing: '1px' }}>
                    <span style={{ display: editingId ? 'inline' : 'none' }}>MODIFIER LA FACTURE</span>
                    <span style={{ display: !editingId ? 'inline' : 'none' }}>ÉMETTRE UNE NOUVELLE FACTURE</span>
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-6">
                                <label className="fw-bold small mb-1 text-muted">CLIENT *</label>
                                <input className="form-control form-control-sm" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} list="clientsListFac" placeholder="Rechercher ou saisir un client..." />
                                <datalist id="clientsListFac">{clients.map(c => <option key={c._id} value={c.name} />)}</datalist>
                            </div>
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">DATE ÉMISSION *</label>
                                <input type="date" className="form-control form-control-sm" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                            </div>
                            <div className="col-12 col-sm-6 col-md-3">
                                <label className="fw-bold small mb-1 text-muted">DATE ÉCHÉANCE</label>
                                <input type="date" className="form-control form-control-sm" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </div>
                        </div>

                        {/* RECHERCHE PRESTATION */}
                        <div className="p-3 mb-4 rounded bg-light border">
                            <label className="fw-bold small mb-2 text-primary">RECHERCHER UNE PRESTATION</label>
                            <div className="position-relative">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-white text-muted border-end-0"><FaSearch/></span>
                                    <input type="text" className="form-control border-start-0" placeholder="Nom ou code..." value={prestationSearchTerm}
                                        onChange={(e) => { setPrestationSearchTerm(e.target.value); setShowSuggestions(true); }}
                                        onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    />
                                    <button type="button" className={`btn fw-bold px-3 text-white ${selectedPrestation ? 'btn-primary' : 'btn-secondary'}`} onClick={handleAddPrestation} disabled={!selectedPrestation}>
                                        <FaPlusCircle className="me-1" /> AJOUTER
                                    </button>
                                </div>
                                {showSuggestions && prestationSearchTerm && (
                                    <div className="position-absolute w-100 shadow bg-white border rounded mt-1" style={{zIndex: 1000, maxHeight: '200px', overflowY: 'auto'}}>
                                        {filteredPrestations.map(p => (
                                            <div key={p._id} className="p-2 border-bottom text-dark bg-light-hover" onMouseDown={() => handleSelectSuggestion(p)} style={{cursor: 'pointer'}}>
                                                <div className="fw-bold">{p.name}</div>
                                                <div className="small text-muted">{p.code} | <span className="fw-bold text-primary">{p.price.toLocaleString()} F</span></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* TABLEAU DES LIGNES */}
                        <div className="table-responsive mb-4 border rounded">
                            <table className="table table-sm mb-0 align-middle table-hover text-nowrap">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3 py-2 text-muted" style={{minWidth: '200px'}}>Prestation</th>
                                        <th className="py-2 text-muted" style={{minWidth: '150px'}}>Détails suppl.</th>
                                        <th className="py-2 text-muted" style={{minWidth: '120px'}}>P.U (F)</th>
                                        <th className="py-2 text-muted" style={{minWidth: '80px'}}>Qté</th>
                                        <th className="text-end py-2 text-muted" style={{minWidth: '120px'}}>Total (F)</th>
                                        <th className="text-center py-2 text-muted" style={{minWidth: '50px'}}>#</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ display: currentLines.length === 0 ? 'table-row' : 'none' }}>
                                        <td colSpan="6" className="text-center py-4 text-muted"><em>Aucune prestation ajoutée.</em></td>
                                    </tr>
                                    {currentLines.map((line, idx) => (
                                        <tr key={`line-${idx}-${line.prestationId || line.code}`}>
                                            <td className="ps-3 fw-bold text-wrap text-dark" style={{minWidth: '200px'}}>{line.name}</td>
                                            <td><input className="form-control form-control-sm" value={line.details} onChange={(e) => updateLine(idx, 'details', e.target.value)} placeholder="..." /></td>
                                            <td><input type="number" className="form-control form-control-sm text-end" value={line.price} onChange={(e) => updateLine(idx, 'price', e.target.value)} /></td>
                                            <td><input type="number" className="form-control form-control-sm text-center" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} /></td>
                                            <td className="text-end fw-bold text-primary">{line.total.toLocaleString()}</td>
                                            <td className="text-center"><button type="button" className="btn btn-sm btn-outline-danger p-1 border-0" onClick={() => removeLine(idx)}><FaTrash size={16}/></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light">
                                        <td colSpan="4" className="text-end py-2 fw-bold text-muted">SOUS-TOTAL :</td>
                                        <td className="text-end py-2 fw-bold text-secondary">{totalHTBrut.toLocaleString()} F</td>
                                        <td></td>
                                    </tr>
                                    <tr className="bg-light border-bottom">
                                        <td colSpan="4" className="text-end py-2 fw-bold text-muted align-middle">REMISE :</td>
                                        <td className="text-end py-1">
                                            <input 
                                                type="number" 
                                                className="form-control form-control-sm text-end text-danger fw-bold ms-auto" 
                                                style={{maxWidth: '120px'}}
                                                value={remise} 
                                                onChange={(e) => setRemise(Math.max(0, Number(e.target.value)))} 
                                                min="0"
                                            />
                                        </td>
                                        <td></td>
                                    </tr>
                                    <tr className="bg-light">
                                        <td colSpan="4" className="text-end py-3 fw-bold text-dark fs-6">TOTAL GÉNÉRAL :</td>
                                        <td className="text-end fs-5 py-3 fw-bold text-primary">{totalTTC.toLocaleString()} F</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pt-3 mt-3 border-top">
                            <div className="text-muted small fst-italic">Champs obligatoires</div>
                            <button 
                                className={`btn px-4 py-2 fw-bold text-white d-flex align-items-center ${currentLines.length === 0 || !clientSearch ? 'btn-secondary' : 'btn-primary'}`} 
                                type="submit" 
                                disabled={currentLines.length === 0 || !clientSearch}
                            >
                                <span style={{ display: editingId ? 'inline-flex' : 'none', alignItems: 'center' }}>
                                    <FaEdit className="me-2"/> METTRE À JOUR
                                </span>
                                <span style={{ display: !editingId ? 'inline-flex' : 'none', alignItems: 'center' }}>
                                    <FaCheckCircle className="me-2"/> VALIDER ET ÉMETTRE
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* HISTORIQUE */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
                    <span className="fw-bold text-dark" style={{ letterSpacing: '1px' }}>
                        <FaHistory className="me-2 text-primary"/>HISTORIQUE
                    </span>
                    <div className="d-flex gap-2">
                        <select className="form-select form-select-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">Tous statuts</option>
                            <option value="émise">Émise</option>
                            <option value="payée">Payée</option>
                            <option value="annulée">Annulée</option>
                        </select>
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                            <input className="form-control border-start-0" placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3">N° Facture</th>
                                <th className="py-3">Client</th>
                                <th className="py-3">Montant TTC</th>
                                <th className="py-3">Date</th>
                                <th className="py-3 text-center">Statut</th>
                                <th className="pe-3 py-3 text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ display: filteredFactures.length === 0 ? 'table-row' : 'none' }}>
                                <td colSpan="6" className="text-center py-4 text-muted">Aucune facture trouvée.</td>
                            </tr>
                            {filteredFactures.map(f => (
                                <tr key={f._id}>
                                    <td className="ps-3 fw-bold text-primary">{f.number}</td>
                                    <td className="text-dark fw-bold">{f.clientName}</td>
                                    <td className="fw-bold text-dark">{f.totalTTC?.toLocaleString()} F</td>
                                    <td className="text-muted small">{new Date(f.date).toLocaleDateString()}</td>
                                    <td className="text-center">
                                        <span className={`badge rounded-1 px-2 py-1 border ${
                                            f.status === 'payée' ? 'bg-success text-success bg-opacity-10 border-success' : 
                                            f.status === 'annulée' ? 'bg-danger text-danger bg-opacity-10 border-danger' :
                                            'bg-warning text-warning-emphasis bg-opacity-10 border-warning'}`}>
                                            {f.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="text-end pe-3">
                                        <button className="btn btn-sm btn-outline-info rounded-1 me-1 py-1 px-2" onClick={() => setViewInvoice(f)} title="Détails"><FaEye size={14}/></button>

                                        {canEditInvoice(f) && (
                                            <button className="btn btn-sm btn-outline-primary rounded-1 me-1 py-1 px-2" onClick={() => startEdit(f)} title="Modifier"><FaEdit size={14}/></button>
                                        )}
                                        
                                        <button className="btn btn-sm btn-outline-danger rounded-1 me-1 py-1 px-2" onClick={() => handleDownloadPDF(f._id, f.number)} title="PDF">
                                            {downloadingPdfId === f._id ? <span className="spinner-border spinner-border-sm" role="status"></span> : <FaFilePdf size={14}/>}
                                        </button>
                                        
                                        {canCancel && f.status !== 'payée' && f.status !== 'annulée' && (
                                            <button className="btn btn-sm btn-outline-warning rounded-1 me-1 py-1 px-2" onClick={() => handleCancel(f)} title="Annuler"><FaBan size={14}/></button>
                                        )}

                                        {canDeleteInvoice(f) && (
                                            <button className="btn btn-sm btn-outline-secondary rounded-1 py-1 px-2" onClick={() => handleDelete(f)} title="Supprimer"><FaTrash size={14}/></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL DE VISUALISATION */}
            {viewInvoice && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header bg-light py-3 border-bottom">
                                <h5 className="modal-title fs-6 fw-bold text-uppercase text-primary">Détails Facture : {viewInvoice.number}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewInvoice(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row mb-4">
                                    <div className="col-12 col-md-6 mb-3 mb-md-0">
                                        <h6 className="fw-bold small mb-1 text-muted">CLIENT FACTURÉ</h6>
                                        <div className="fs-6 fw-bold text-dark">{viewInvoice.clientName}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <h6 className="fw-bold small mb-1 text-muted">DATE ÉMISSION</h6>
                                        <div className="fw-bold text-dark">{new Date(viewInvoice.date).toLocaleDateString('fr-FR')}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                        <h6 className="fw-bold small mb-1 text-muted">STATUT</h6>
                                        <span className={`badge rounded-1 px-3 py-2 border ${
                                            viewInvoice.status === 'payée' ? 'bg-success text-success bg-opacity-10 border-success' : 
                                            viewInvoice.status === 'annulée' ? 'bg-danger text-danger bg-opacity-10 border-danger' :
                                            'bg-warning text-warning-emphasis bg-opacity-10 border-warning'}`}>
                                            {viewInvoice.status.toUpperCase()}
                                        </span>
                                    </div>
                                </div>

                                <div className="table-responsive border rounded">
                                    <table className="table table-sm mb-0 table-hover align-middle">
                                        <thead className="bg-light small text-uppercase text-muted">
                                            <tr>
                                                <th className="ps-3 py-2">Prestation / Libellé</th>
                                                <th className="text-end py-2">P.U (FCFA)</th>
                                                <th className="text-center py-2">Qté</th>
                                                <th className="text-end pe-3 py-2">Total (FCFA)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewInvoice.lines?.map((l, i) => (
                                                <tr key={i}>
                                                    <td className="ps-3 py-2 text-wrap" style={{minWidth: '200px'}}>
                                                        <div className="fw-bold text-dark">{l.name}</div>
                                                        {l.details && <div className="text-muted small fst-italic">{l.details}</div>}
                                                    </td>
                                                    <td className="text-end py-2 text-dark">{l.price?.toLocaleString()}</td>
                                                    <td className="text-center py-2 text-dark">{l.quantity}</td>
                                                    <td className="text-end pe-3 py-2 fw-bold text-primary">{l.total?.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-light">
                                                <td colSpan="3" className="text-end py-2 fw-bold text-muted">SOUS-TOTAL :</td>
                                                <td className="text-end pe-3 py-2 fw-bold text-secondary">
                                                    {(viewInvoice.totalHT + (viewInvoice.remise || 0)).toLocaleString()} F
                                                </td>
                                            </tr>
                                            <tr className="bg-light text-danger">
                                                <td colSpan="3" className="text-end py-2 fw-bold">REMISE :</td>
                                                <td className="text-end pe-3 py-2 fw-bold">-{viewInvoice.remise?.toLocaleString()} F</td>
                                            </tr>
                                            <tr className="bg-light">
                                                <td colSpan="3" className="text-end py-3 fw-bold text-dark">MONTANT TOTAL TTC :</td>
                                                <td className="text-end pe-3 py-3 fs-5 fw-bold text-primary">{viewInvoice.totalTTC?.toLocaleString()} F</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer py-3 border-top bg-light">
                                <button className="btn btn-sm btn-secondary px-4 fw-bold" onClick={() => setViewInvoice(null)}>FERMER</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FacturePage;